/** Bulk action bar component for CDRs page. */
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  LinearProgress,
} from "@mui/material";
import { Slide, SlideProps } from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { TransitionProps } from "@mui/material/transitions";

interface BulkActionBarProps {
  selectedCount: number;
  onExport: () => void;
  onRerate: () => void;
  onMarkPaid: () => void;
  onMarkUnpaid: () => void;
  onClearSelection: () => void;
}

/** Slide transition for the action bar. */
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

/** Bulk action bar that appears at bottom when rows are selected. */
export default function BulkActionBar({
  selectedCount,
  onExport,
  onRerate,
  onMarkPaid,
  onMarkUnpaid,
  onClearSelection,
}: BulkActionBarProps) {
  const [rerateDialogOpen, setRerateDialogOpen] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  // Wave 3.4 (review §2.2): say WHICH operation is running and for how long.
  // Server-side bulk ops are single requests, so per-item % is not honestly
  // available; we show operation label + live elapsed time instead.
  const [operation, setOperation] = useState<{ label: string; startedAt: number } | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!operation) return;
    setElapsed(0);
    const timer = setInterval(() => {
      setElapsed(Math.round((Date.now() - operation.startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [operation]);

  const runOperation = async (label: string, fn: () => void | Promise<void>) => {
    setOperation({ label, startedAt: Date.now() });
    setOperationInProgress(true);
    try {
      await fn();
    } finally {
      setOperationInProgress(false);
      setOperation(null);
    }
  };

  /** Handle rerate with confirmation. */
  const handleRerateClick = () => {
    setRerateDialogOpen(true);
  };

  /** Confirm and execute rerate. */
  const handleRerateConfirm = async () => {
    setRerateDialogOpen(false);
    await runOperation("Rerating", onRerate);
  };

  /** Don't show bar if nothing selected. */
  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <AppBar
        position="fixed"
        color="primary"
        sx={{
          top: "auto",
          bottom: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar variant="dense" sx={{ flexWrap: "wrap", rowGap: 1, py: 1 }}>
          <Typography variant="body1" sx={{ flexGrow: 1, fontWeight: "bold" }}>
            {selectedCount} CDR{selectedCount !== 1 ? "s" : ""} selected
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* Export */}
            <IconButton
              color="inherit"
              onClick={() => runOperation("Exporting", onExport)}
              disabled={operationInProgress}
              title="Export to CSV"
             aria-label="Download">
              <DownloadIcon />
            </IconButton>

            {/* Rerate */}
            <IconButton
              color="inherit"
              onClick={handleRerateClick}
              disabled={operationInProgress}
              title="Rerate selected CDRs"
             aria-label="Refresh">
              <RefreshIcon />
            </IconButton>

            {/* Mark Paid */}
            <IconButton
              color="inherit"
              onClick={() => runOperation("Marking paid", onMarkPaid)}
              disabled={operationInProgress}
              title="Mark as Paid"
             aria-label="Check circle">
              <CheckCircleIcon />
            </IconButton>

            {/* Mark Unpaid */}
            <IconButton
              color="inherit"
              onClick={() => runOperation("Marking unpaid", onMarkUnpaid)}
              disabled={operationInProgress}
              title="Mark as Unpaid"
             aria-label="Cancel">
              <CancelIcon />
            </IconButton>

            {/* Clear Selection */}
            <Button
              color="inherit"
              onClick={onClearSelection}
              disabled={operationInProgress}
              startIcon={<CloseIcon />}
              sx={{ ml: 1 }}
            >
              Clear Selection
            </Button>
          </Box>
        </Toolbar>

        {/* Progress feedback during bulk operations (Wave 3.4) */}
        {operationInProgress && operation && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
              {operation.label} {selectedCount} CDR{selectedCount !== 1 ? "s" : ""}… {elapsed}s
            </Typography>
            <LinearProgress
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
              }}
            />
          </Box>
        )}
      </AppBar>

      {/* 3.8: layout spacer — the bar is fixed, so page content underneath
          would otherwise be hidden behind it while a selection is active */}
      <Box sx={{ height: 64 }} />

      {/* Rerate confirmation dialog */}
      <Dialog
        open={rerateDialogOpen}
        TransitionComponent={SlideTransition}
        keepMounted
        onClose={() => setRerateDialogOpen(false)}
      >
        <DialogTitle>Confirm Rerate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to rerate {selectedCount} CDR
            {selectedCount !== 1 ? "s" : ""}? This will recalculate costs based on
            current rating plans.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRerateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRerateConfirm} variant="contained" color="primary">
            Rerate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
